import * as React from 'react';
import { DatePickerRange, Form, CustomSubmitButton, Select, TextInput, Checkbox, ClearFormButton, CustomSubmitButtonType } from "soprasteria-ui-library";
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { GeneralDentalReferralSearchApi } from '../../../api/GeneralDentalReferralSearchApi';
import { useAuth } from '../../../App';
import { IGeneralDentalReferralSearchParams } from '../../../models/IGeneralDentalReferral';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { TANDREGLERINGBAS, VÅRDANSVARIG, LIBRETTOADMIN } from '../../../utils/RoleConstants';
import { ICareProvider } from '../../../models/ICareProvider';
interface IGeneralDentalReferralSearchForm {
    preSetSearchParams?: IGeneralDentalReferralSearchParams;
    searchGeneralDentalReferrals: (data: IGeneralDentalReferralSearchParams) => void;
}

export const GeneralDentalReferralSearchForm: React.FunctionComponent<IGeneralDentalReferralSearchForm> = ({ searchGeneralDentalReferrals, preSetSearchParams }) => {
    const auth = useAuth();
    const api = new GeneralDentalReferralSearchApi(auth);
    const [generalDentalReferralStatuses, setGeneralDentalReferralStatuses] = React.useState<IListItem[]>();

    const [referrerUmbrellaOrganisations, setReferrerUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [referrerCareProviders, setReferrerCareProviders] = React.useState<ICareProvider[]>();
    const [resetReferrerCareProviderValue, doResetReferrerCareProviderValue] = React.useState<boolean>(false);
    const [isFetchingReferrerUmbrellaOrganisations, setIsFetchingReferrerUmbrellaOrganisations] = React.useState<boolean>(false);
    const [selectedReferrerUmbrellaOrganisationId, setSelectedReferrerUmbrellaOrganisationId] = React.useState<number>();

    const [recipientUmbrellaOrganisations, setRecipientUmbrellaOrganisations] = React.useState<ICareProvider[]>();
    const [recipientCareProviders, setRecipientCareProviders] = React.useState<ICareProvider[]>();
    const [resetRecipientCareProviderValue, doResetRecipientCareProviderValue] = React.useState<boolean>(false);
    const [isFetchingRecipientUmbrellaOrganisations, setIsFetchingRecipientUmbrellaOrganisations] = React.useState<boolean>(false);
    const [selectedRecipientUmbrellaOrganisationId, setSelectedRecipientUmbrellaOrganisationId] = React.useState<number>();

    const [onlyShowGeneralDentalReferralsWithUnreadAnswer, setOnlyShowGeneralDentalReferralsWithUnreadAnswer] = React.useState<boolean>()
    const [isExportingToExcel, setIsExportingToExcel] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleOrtodontist = isInRole(TANDREGLERINGBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleSystemAdministrator = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);
    const abortController = new AbortController();


    React.useEffect(() => {
        getGeneralDentalReferralStatuses();
        getReferrerUmbrellaOrganisations();
        getRecipientUmbrellaOrganisations();

        return () => {
            abortController.abort();
        }
    }, [])

    React.useEffect(() => {
        if (preSetSearchParams?.onlyShowGeneralDentalReferralsWithUnreadAnswer) {
            setOnlyShowGeneralDentalReferralsWithUnreadAnswer(true);
        }
    }, [preSetSearchParams])

    const getGeneralDentalReferralStatuses = () => {
        api.getGeneralDentalReferralStatuses((statuses) => {
            const statusListItems = statuses.map<IListItem>((status) => ({ text: status.description, value: status.statusId.toString() }));
            setGeneralDentalReferralStatuses(statusListItems);
        });
    }

    const getReferrerCareProviders = () => {
        api.getReferrerCareProviders((careProviers) => setReferrerCareProviders(careProviers))
    }
    const getRecipientCareProviders = () => {
        api.getRecipientCareProviders((careProviers) => setRecipientCareProviders(careProviers))
    }

    const getReferrerUmbrellaOrganisations = () => {
        setIsFetchingReferrerUmbrellaOrganisations(true);
        api.getReferrerUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.referrerUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams.referrerUmbrellaOrganisationId.toString());
                getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            } else if (!hasRoleSystemAdministrator && umbrellaOrganisations.length === 1) {
                umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedReferrerUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }
            setReferrerUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingReferrerUmbrellaOrganisations(false);
        });
    }
    const getReferrerCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingReferrerUmbrellaOrganisations(true);

        api.getReferrerCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setReferrerCareProviders(careProviders);
            } else {
                setReferrerCareProviders(undefined);
            }
            setIsFetchingReferrerUmbrellaOrganisations(false);
        }, undefined, () => { });
    }
    const onValueChangeReferrerUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getReferrerCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            getReferrerCareProviders();
        }
        doResetReferrerCareProviderValue(prev => !prev);
    }
    const getReferrerUmbrellaOrganisationDefaultValue = () => {
        if (referrerUmbrellaOrganisations && preSetSearchParams?.referrerUmbrellaOrganisationId) {
            return preSetSearchParams.referrerUmbrellaOrganisationId.toString();
        }

        if (selectedReferrerUmbrellaOrganisationId) {
            return selectedReferrerUmbrellaOrganisationId.toString();
        }

        if (referrerUmbrellaOrganisations?.length === 1) {
            return referrerUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }
    const getReferrerCareProviderDefaultValue = () => {
        if (referrerCareProviders) {

            if (referrerCareProviders && referrerCareProviders.length === 1 && referrerCareProviders[0] !== undefined) {
                return referrerCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }

    const getRecipientUmbrellaOrganisations = () => {
        setIsFetchingRecipientUmbrellaOrganisations(true);
        api.getRecipientUmbrellaOrganisations((umbrellaOrganisations) => {
            let umbrellaOrganisationIdToUse;
            if (preSetSearchParams?.recipientUmbrellaOrganisationId) {
                umbrellaOrganisationIdToUse = parseInt(preSetSearchParams?.recipientUmbrellaOrganisationId.toString());
                setSelectedRecipientUmbrellaOrganisationId(umbrellaOrganisationIdToUse)

                getRecipientCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            } else if (!hasRoleSystemAdministrator && umbrellaOrganisations.length === 1) {
                umbrellaOrganisationIdToUse = umbrellaOrganisations[0].careProviderId;
                setSelectedRecipientUmbrellaOrganisationId(umbrellaOrganisations[0].careProviderId);
                getRecipientCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationIdToUse, umbrellaOrganisations);
            }

            setRecipientUmbrellaOrganisations(umbrellaOrganisations);
        }, undefined, () => {
            setIsFetchingRecipientUmbrellaOrganisations(false);
        });
    }
    const getRecipientCareProvidersByUmbrellaOrganisationId = (umbrellaOrganisationId: number, umbrellaOrganisationsAlt?: ICareProvider[]) => {
        setIsFetchingRecipientUmbrellaOrganisations(true);

        api.getRecipientCareProvidersByUmbrellaOrganisationId(umbrellaOrganisationId, (careProviders) => {
            if (careProviders.length !== 0) {
                setRecipientCareProviders(careProviders);
            } else {
                setRecipientCareProviders(undefined);
            }
            setIsFetchingRecipientUmbrellaOrganisations(false);
        }, undefined, () => { });
    }
    const onValueChangeRecipientUmbrellaOrganisations = (selectedUmbrellaOrganisation: string | undefined) => {       
        if (selectedUmbrellaOrganisation && selectedUmbrellaOrganisation !== "") {
            getRecipientCareProvidersByUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
            setSelectedRecipientUmbrellaOrganisationId(parseInt(selectedUmbrellaOrganisation));
        }
        else {
            setSelectedRecipientUmbrellaOrganisationId(undefined);
            getRecipientCareProviders();
        }
        doResetRecipientCareProviderValue(prev => !prev);    
    }
    const getRecipientUmbrellaOrganisationDefaultValue = () => {
        if (recipientUmbrellaOrganisations && preSetSearchParams?.recipientUmbrellaOrganisationId) {
            return preSetSearchParams.recipientUmbrellaOrganisationId.toString();
        }

        if (selectedRecipientUmbrellaOrganisationId) {
            return selectedRecipientUmbrellaOrganisationId.toString();
        }

        if (recipientUmbrellaOrganisations?.length === 1) {
            return recipientUmbrellaOrganisations[0].careProviderId.toString();
        }

        return undefined;
    }
    const getRecipientCareProviderDefaultValue = () => {
        if (recipientCareProviders) {
            if (recipientCareProviders && recipientCareProviders.length === 1 && recipientCareProviders[0] !== undefined) {
                return recipientCareProviders[0].careProviderId.toString();
            }
        }

        return undefined;
    }



    const exportSearchResultToExcel = (data: IGeneralDentalReferralSearchParams) => {
        setIsExportingToExcel(true);

        api.searchGeneralDentalReferrals(data, true, undefined, "Allmäntandvårdsremisser", undefined, () => setIsExportingToExcel(false));
    }

    return (
        <section>
            <Form>
                <div className="row">
                    <div className="col-6">
                        <DatePickerRange label="Datum" name="sentDateFrom" nameSecondary="sentDateTo" inlineLabel
                            value={preSetSearchParams?.sentDateFrom ? new Date(preSetSearchParams.sentDateFrom) : undefined}
                            valueSecondary={preSetSearchParams?.sentDateTo ? new Date(preSetSearchParams.sentDateTo) : undefined} />
                        <TextInput name="generalDentalReferralNumber" label="Remiss-ID" inlineLabel defaultValue={preSetSearchParams?.generalDentalReferralNumber} />
                        {
                            !onlyShowGeneralDentalReferralsWithUnreadAnswer ?
                                <Select name="generalDentalReferralStatusId"
                                    label="Status"
                                    placeholder="Välj en status"
                                    inlineLabel
                                    isLoading={generalDentalReferralStatuses === undefined}
                                    selectedValue={preSetSearchParams?.generalDentalReferralStatusId?.toString()}
                                    options={generalDentalReferralStatuses ?? []}
                                    isClearable />
                                : null
                        }
                    </div>
                    <div className="col-6">
                        <CareProviderDropdown
                            label="Remittent vårdgivare"
                            name="referrerUmbrellaOrganisationId"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingReferrerUmbrellaOrganisations}
                            careProviders={referrerUmbrellaOrganisations}
                            onValueChange={onValueChangeReferrerUmbrellaOrganisations}
                            defaultValue={getReferrerUmbrellaOrganisationDefaultValue()}
                            isClearable={referrerUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />

                        <CareProviderDropdown
                            label={"Remittent mottagning"}
                            name="referrerCareProviderId"
                            isLoading={referrerCareProviders === undefined}
                            careProviders={referrerCareProviders}
                            defaultValue={getReferrerCareProviderDefaultValue()}
                            isClearable={referrerCareProviders?.length !== 1}
                            showClinicId
                            resetValue={resetReferrerCareProviderValue}
                            
                        />
                        <CareProviderDropdown
                            name="recipientUmbrellaOrganisationId"
                            label="Mottagande vårdgivare"
                            placeholder="Välj vårdgivare"
                            isLoading={isFetchingRecipientUmbrellaOrganisations}
                            careProviders={recipientUmbrellaOrganisations}
                            defaultValue={getRecipientUmbrellaOrganisationDefaultValue()}
                            onValueChange={onValueChangeRecipientUmbrellaOrganisations}
                            isClearable={recipientUmbrellaOrganisations?.length !== 1}
                            showOrgNum
                        />
                        <CareProviderDropdown
                            name="recipientCareProviderId"
                            label={"Mottagande mottagning"}
                            isLoading={recipientCareProviders === undefined}
                            careProviders={recipientCareProviders}
                            defaultValue={getRecipientCareProviderDefaultValue()}
                            isClearable={recipientUmbrellaOrganisations?.length !== 1}
                            showClinicId
                            resetValue={resetRecipientCareProviderValue}
                        />
                        {
                            hasRoleOrtodontist ?
                                <Checkbox
                                    name="onlyShowGeneralDentalReferralsWithUnreadAnswer"
                                    id="onlyShowGeneralDentalReferralsWithUnreadAnswer"
                                    label="Visa bara remisser med remissvar ej läst"
                                    value="true"
                                    checked={onlyShowGeneralDentalReferralsWithUnreadAnswer}
                                    onChange={(changedValue) => setOnlyShowGeneralDentalReferralsWithUnreadAnswer(changedValue)} />
                                : null
                        }
                    </div>
                    <div className="col-12">
                        <div className="text-right panel">
                            <span className="d-none">
                                <CustomSubmitButton onButtonSubmit={searchGeneralDentalReferrals}>Sök</CustomSubmitButton>
                            </span>
                            <CustomSubmitButton buttonType={CustomSubmitButtonType.alternative} onButtonSubmit={exportSearchResultToExcel} isLoading={isExportingToExcel}><FontAwesomeIcon icon={faFileExcel} /> Exportera till excel</CustomSubmitButton>
                            <ClearFormButton />
                            <CustomSubmitButton onButtonSubmit={searchGeneralDentalReferrals}>Sök</CustomSubmitButton>
                        </div>
                    </div>
                </div>
            </Form>
        </section>
    )
}